import { useState, useEffect } from "react";
import { Alert, Avatar, message, Button, Slider } from "antd";
import {
  CheckCircleOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import garageMoneyIcon from "../../../../Assets/Icons/garage_money.svg";
import laptopCarIcon from "../../../../Assets/Icons/laptop_car.svg";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  finalizeCreditSettlement,
  getManagerCredits,
  recalculateCreditAllocation,
} from "../../../../API/FleetManager/CreditsBillings";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const DynamicAllocation = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("credits-billing");
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const [managerCredits, setManagerCredits] = useState(null);
  const [allocationPreview, setAllocationPreview] = useState(null);
  const [distributeBlock, setDistributeBlock] = useState(null);

  const fetchManagerCredits = async () => {
    try {
      const res = await getManagerCredits();

      if (Math.floor(res?.status / 100) === 2) {
        setManagerCredits(res?.data?.data || null);
      }
    } catch (err) {
      console.error("Failed to load manager credits:", err);
    }
  };

  const fetchAllocationPreview = async () => {
    try {
      const res = await getManagerCredits("dynamic");

      if (Math.floor(res?.status / 100) === 2) {
        setAllocationPreview(res?.data?.data || null);
        setDistributeBlock(null);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (
        data?.code === "no_credit_balance" ||
        data?.code === "no_active_vehicles"
      ) {
        setAllocationPreview(null);
        setDistributeBlock({ code: data.code, message: data.message });
        return;
      }
      console.error("Failed to load dynamic allocation preview:", err);
    }
  };

  useEffect(() => {
    fetchManagerCredits();
    fetchAllocationPreview();
  }, []);

  const used = parseFloat(managerCredits?.allocated_total) || 0;
  const limit =
    parseFloat(managerCredits?.total_balance?.value ?? managerCredits?.total_balance) ||
    0;
  const available = parseFloat(managerCredits?.unallocated_balance) || 0;
  const percentageUsed = limit ? (used / limit) * 100 : 0;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Slider State
  const [sensitivity, setSensitivity] = useState(0.85);

  const mapAllocationRow = (allocation) => ({
    key: String(allocation.vehicle_id),
    vehicle: allocation.vehicle || "-",
    plateNumber: allocation.license_plate || "-",
    driverName: allocation.driver || "-",
    driverAvatar: resolveAvatarUrl(allocation.driver_avatar),
    module: Array.isArray(allocation.assigned_modules)
      ? allocation.assigned_modules
      : allocation.assigned_modules
        ? [allocation.assigned_modules]
        : [],
    fuelUsage:
      allocation.fuel_usage !== undefined && allocation.fuel_usage !== null
        ? allocation.fuel_usage
        : null,
    creditAllocated: parseFloat(allocation.amount) || 0,
    status: allocation.status || "-",
  });

  const handleRecalculate = async () => {
    message.loading({
      content: "Recalculating allocation logic...",
      key: "recalc",
    });

    try {
      const res = await recalculateCreditAllocation("dynamic", sensitivity);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;

        setAllocationPreview(data || null);
        if (data?.sensitivity !== undefined) {
          setSensitivity(Number(data.sensitivity));
        }

        message.success({
          content:
            res?.data?.message || "Dynamic allocations refreshed successfully!",
          key: "recalc",
          duration: 2,
        });
      } else {
        message.error({
          content: getErrorMessage(
            res?.data,
            "Failed to recalculate allocations.",
          ),
          key: "recalc",
        });
      }
    } catch (err) {
      message.error({
        content: getErrorMessage(
          err?.response?.data,
          "Failed to recalculate allocations.",
        ),
        key: "recalc",
      });
    }
  };

  const handleView = (record) => {
    navigate("/credit-adjustments-manager", {
      state: { from: "Dynamic Allocation", vehicleId: record.key },
    });
  };

  const [creditsData, setCreditsData] = useState([]);

  useEffect(() => {
    setCreditsData((allocationPreview?.rows || []).map(mapAllocationRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationPreview]);

  const creditsColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: "true",
      render: (text) => (
        <span style={{ fontWeight: 700, color: "#111827" }}>{text}</span>
      ),
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      enableSearch: "true",
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: "true",
      render: (_, record) =>
        record.driverAvatar ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar src={record.driverAvatar} size="small" />
            <span style={{ fontSize: "14px", color: "#3F3F46" }}>
              {record.driverName}
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar
              size="small"
              style={{
                background: getAvatarColor(record.driverName),
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {getInitials(record.driverName)}
            </Avatar>
            <span style={{ fontSize: "14px", color: "#3F3F46" }}>
              {record.driverName}
            </span>
          </div>
        ),
    },
    {
      title: "Assign Module",
      dataIndex: "module",
      key: "module",
      render: (modules) =>
        Array.isArray(modules) && modules.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {modules.map((module) => (
              <span
                key={module}
                className="assigned-module-badge"
                style={getModuleBadgeStyle(module)}
              >
                {module}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Fuel Usage (6M)",
      dataIndex: "fuelUsage",
      key: "fuelUsage",
      render: (text) => text || "-",
    },
    {
      title: "Credit Allocated",
      dataIndex: "creditAllocated",
      key: "creditAllocated",
      sorter: (a, b) => (a.creditAllocated || 0) - (b.creditAllocated || 0),
      render: (value) => (
        <span style={{ color: "#111827", fontWeight: 700 }}>
          $
          {Number(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [...new Set(creditsData.map((item) => item.status))].map(
        (status) => ({
          text: status,
          value: status,
        }),
      ),
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Active" ? "active" : "locked"}`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <EyeOutlined
            style={{
              cursor: "pointer",
              color: "#4F46E5",
              fontSize: 18,
            }}
            onClick={() => handleView(record)}
          />
        </div>
      ),
    },
  ];

  const handleFinalizeSettlement = async () => {
    try {
      const res = await finalizeCreditSettlement("dynamic");

      if (Math.floor(res?.status / 100) === 2) {
        setAllocationPreview(res?.data?.data || null);
        message.success(res?.data?.message || "Settlement Finalized");
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to finalize settlement."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to finalize settlement.",
        ),
      );
    }
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Finalize Settlement",
        icon: <CheckCircleOutlined />,
        onClick: handleFinalizeSettlement,
        disabled: !!distributeBlock,
        title: distributeBlock?.message,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs, distributeBlock]);

  return (
    <div className="credits-billings-page">
      {distributeBlock && (
        <Alert
          className="distribute-block-alert"
          type="warning"
          showIcon
          message={distributeBlock.message}
        />
      )}
      <div className="row">
        <div className="stats-container">
          <GenericStatCard
            title="Available to Allocate"
            value={`$${available.toFixed(2)}`}
            icon={garageMoneyIcon}
          />
          <GenericStatCard
            title="Total Active Vehicles"
            value={managerCredits?.vehicle_count?.value ?? 0}
            {...(managerCredits?.vehicle_count?.percentage != null && {
              percentage: `${managerCredits.vehicle_count.percentage}`,
            })}
            isIncrease={managerCredits?.vehicle_count?.is_increase}
            icon={laptopCarIcon}
          />
        </div>
      </div>
      <div className="row credit-row">
        <div className="tabs card">
          <div className="credit-card">
            <div className="credit-title">Credit Utilization in JMD</div>

            <div className="credit-bar-section">
              <span className="label-card">Available Credits</span>
              <span className="available-amount">
                ${available.toLocaleString()}
              </span>
            </div>

            <div className="credit-bar">
              <div
                className="credit-bar-fill"
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>

            <div className="credit-footer">
              <span>
                Used:<span> ${used.toLocaleString()}</span>
              </span>
              <span>
                Limit: <span>${limit.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "16px",
          marginTop: "24px",
        }}
      >
        <div
          style={{
            flex: "1",
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #E4E4E7",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#71717A",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "20px",
            }}
          >
            Dynamic Allocation Method
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "13px",
              fontWeight: "600",
              color: "#3F3F46",
            }}
          >
            <span>Adjust Sensitivity</span>
            <span style={{ color: "#4F46E5" }}>{sensitivity}x</span>
          </div>

          <Slider
            min={0}
            max={2}
            step={0.01}
            value={sensitivity}
            onChange={(val) => setSensitivity(val)}
            tooltip={{ open: false }}
            trackStyle={{ background: "#4F46E5", height: "6px" }}
            handleStyle={{
              borderColor: "#4F46E5",
              width: "16px",
              height: "16px",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "10px",
              fontWeight: "700",
              color: "#A1A1AA",
              textTransform: "uppercase",
              marginTop: "-4px",
              marginBottom: "20px",
            }}
          >
            <span>Conservative</span>
            <span>Aggressive</span>
          </div>

          {canEdit && (
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleRecalculate}
              disabled={!!distributeBlock}
              style={{
                width: "100%",
                background: "#4F46E5",
                borderColor: "#4F46E5",
                height: "42px",
                borderRadius: "8px",
                fontWeight: "600",
              }}
            >
              Recalculate Allocation
            </Button>
          )}
        </div>

        <div
          style={{
            flex: "1",
            background: "#2170E41A",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid #0058BE33",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#4F46E5",
              fontWeight: "700",
              fontSize: "14px",
              marginBottom: "10px",
            }}
          >
            <InfoCircleOutlined />
            <span>Dynamic Logic Active</span>
          </div>
          <p
            style={{
              fontSize: "14px",
              color: "#3F3F46",
              lineHeight: "1.5",
              margin: "0 0 12px 0",
              fontWeight: "500",
            }}
          >
            Credit is allocated based on historical fuel usage{" "}
            <strong>(last 6 months)</strong>.
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "#71717A",
              fontStyle: "italic",
              margin: "0",
            }}
          >
            Allocations auto-balance to maintain a 0% remaining credit buffer.
          </p>
        </div>
      </div>

      <div className="credit-table">
        <GenericTable
          dataSource={creditsData}
          columns={creditsColumns}
          enableRowSelection={false}
        />
      </div>
    </div>
  );
};

export default DynamicAllocation;
